/**
 * Single source of truth for the shape of booking-component URLs.
 *
 * Background
 * ----------
 * The booking flow is served by the Shopify page `/pages/booking` (formerly
 * `/pages/main-go-karting-track`) and mirrors its router state into the path:
 *
 *   /pages/booking/main/seats?seats=1&races=3&age=15%2B&pc=2000
 *
 * Older links carried the router state in the query string instead:
 *
 *   /pages/main-go-karting-track/main-track/seats-races
 *     ?numberOfSeats=1&numberOfRaces=3&ageGroup=15%2B
 *     &selectedRoute=main-track&selectedScreen=seats-races&postcode=2000
 *
 * `selectedRoute`/`selectedScreen` duplicated the path segments. They existed
 * because Shopify only serves the single-segment page handle: a hard load of
 * `/pages/<handle>/main-track/seats-races` 404s, and the theme's 404 redirect
 * kept only the query string. The query params were therefore the real
 * deep-link carrier and the path was decorative.
 *
 * What this module does
 * ---------------------
 * 1. Builds routing on top of whatever served the app — the booking page
 *    (`/pages/booking` or the legacy handle) or another mount page — and
 *    never rewrites the base part of the pathname. The page the user landed
 *    on is the page they see. The one exception is the `/booking/tracks`
 *    alias, which no page serves (see 5).
 * 2. Moves the deep link back onto the path and drops the duplicated
 *    `selectedRoute`/`selectedScreen` params.
 * 3. Rewrites superseded route/screen slugs and query keys to their short
 *    canonical form, so `/racing-games-track?numberOfSeats=2` still resolves.
 * 4. Survives hard loads: a hard load of any deep URL 404s, the theme redirect
 *    stashes the requested path in `sessionStorage` before bouncing to the
 *    real Shopify page, and `resolveBookingLocation` replays it so the user
 *    lands on the right screen with the full URL restored on the same page.
 * 5. Keeps the `/booking/tracks` alias working as an inbound spelling only:
 *    marketing links still point there and the theme bounces them to the
 *    booking page, but the alias resolves to the `/pages/booking` base
 *    instead of being restored, so the public base is always the page.
 * 6. Track-specific mount pages (junior-track, kids-go-karting-track,
 *    battle-kart-booking) own their track: a deep link that names a different
 *    track under such a page is rewritten to the page's own route, keeping
 *    the screen — `/pages/junior-track/intermediate/seats` resolves to
 *    `/pages/junior-track/junior/seats`.
 * 7. The age-group picker's slug is `tracks` (`/pages/booking/tracks`). It was
 *    briefly `racing`, because on the alias base the `tracks` spelling doubled
 *    up (`/booking/tracks` + `tracks`) — moot now that the base is the page.
 *    Both `racing` and the original `racing-games-track` are legacy aliases
 *    that self-heal to `/pages/booking/tracks`.
 *
 * Everything runs before the router mounts (see `src/index.tsx`), so React
 * Router only ever sees the canonical in-app path.
 */

/**
 * Pretty alias that marketing links still point at. Shopify cannot serve it,
 * so the theme bounces those requests to the booking page; whenever an alias
 * URL reaches the app (dev server, headless embed, or the theme's handoff) it
 * resolves to `BOOKING_PAGE_PATH` rather than being kept as the base.
 */
export const BOOKING_ALIAS_PATH = "/booking/tracks";

/**
 * Shopify page handles that serve the booking flow. The legacy handle stays
 * listed so old links (and Shopify's automatic handle-change redirect) keep
 * working. Each handle keeps its own pathname — routing builds on top of the
 * serving page and the base is never swapped for a different page.
 */
export const BOOKING_PAGE_HANDLES = ["booking", "main-go-karting-track"];

/**
 * The public base for the booking flow, and the only base anything is ever
 * rewritten *onto* — the alias resolves here. The legacy handle stays
 * servable, so a visitor already on it keeps it; nothing is moved there.
 */
export const BOOKING_PAGE_PATH = `/pages/${BOOKING_PAGE_HANDLES[0]}`;

/**
 * Handoff slot used by the theme's 404 redirect. The theme cannot render a
 * multi-segment path, so it records where the user *wanted* to go, sends them
 * to the real page, and we restore the path here.
 *
 * Keep in sync with `snippets/booking-url-redirect.liquid` in
 * `hyperkarting-shopify-new`, which owns the bounce from the alias and deep
 * URLs to the booking page.
 */
export const DEEP_PATH_STORAGE_KEY = "hk_booking_deep_path";

/** Query params that duplicated the path and are no longer written to URLs. */
export const LEGACY_ROUTING_PARAMS = ["selectedRoute", "selectedScreen"] as const;

/**
 * Canonical in-app route slugs — the first path segment after the base path.
 * `TRACKS` is the age-group/experience picker, spelled `racing-games-track`
 * originally and `racing` while the alias was the public base; both older
 * spellings are translated during bootstrap.
 */
export const ROUTE_SLUGS = {
  TRACKS: "tracks",
  MAIN: "main",
  INTERMEDIATE: "intermediate",
  MINI: "mini",
  JUNIOR: "junior",
  BATTLE_KART: "battlekart",
  ARCADE: "arcade",
} as const;

/** Canonical in-app screen slugs — the second path segment. */
export const SCREEN_SLUGS = {
  SEATS: "seats",
  PLAYERS: "players",
  LOCATION: "location",
  DATE: "date",
  SUMMARY: "summary",
  EXTRAS: "extras",
  CARD: "card",
  EXPERIENCE: "experience",
  CART: "cart",
} as const;

/**
 * Old → canonical slug aliases.
 *
 * The long slugs are still live in marketing links (the homepage cards and the
 * details pages in the theme both hardcode them) and in anything users have
 * bookmarked or search engines have indexed, so they are translated to the
 * canonical form during bootstrap rather than routed separately. Nothing
 * downstream of `resolveBookingLocation` ever sees a legacy slug.
 */
export const LEGACY_ROUTE_SLUGS: Readonly<Record<string, string>> = {
  "racing-games-track": ROUTE_SLUGS.TRACKS,
  racing: ROUTE_SLUGS.TRACKS,
  "main-track": ROUTE_SLUGS.MAIN,
  "intermediate-track": ROUTE_SLUGS.INTERMEDIATE,
  "mini-track": ROUTE_SLUGS.MINI,
  "junior-track": ROUTE_SLUGS.JUNIOR,
  "battle-kart-track": ROUTE_SLUGS.BATTLE_KART,
};

export const LEGACY_SCREEN_SLUGS: Readonly<Record<string, string>> = {
  "seats-races": SCREEN_SLUGS.SEATS,
  "players-sessions": SCREEN_SLUGS.PLAYERS,
  "post-code": SCREEN_SLUGS.LOCATION,
  "date-time": SCREEN_SLUGS.DATE,
  "booking-summary": SCREEN_SLUGS.SUMMARY,
  "add-another-attraction": SCREEN_SLUGS.EXTRAS,
  "arcade-card": SCREEN_SLUGS.CARD,
  "review-experience": SCREEN_SLUGS.EXPERIENCE,
  "review-cart": SCREEN_SLUGS.CART,
};

/** Route slugs that name an actual bookable track (everything except the age-group picker). */
const TRACK_ROUTE_SLUGS: ReadonlySet<string> = new Set([
  ROUTE_SLUGS.MAIN,
  ROUTE_SLUGS.INTERMEDIATE,
  ROUTE_SLUGS.MINI,
  ROUTE_SLUGS.JUNIOR,
  ROUTE_SLUGS.BATTLE_KART,
  ROUTE_SLUGS.ARCADE,
]);

/**
 * Track-specific mount pages and the in-app track route each one owns. These
 * pages exist to land visitors on one track's flow, so a deep link naming a
 * *different* track under such a page is contradictory — the page wins.
 * Mirrors the handle → track mapping used for first-visit redirects in
 * `TrackSelection`.
 */
const MOUNT_PAGE_TRACK_ROUTES: Readonly<Record<string, string>> = {
  "kids-go-karting-track": ROUTE_SLUGS.MINI,
  "junior-track": ROUTE_SLUGS.JUNIOR,
  "battle-kart-booking": ROUTE_SLUGS.BATTLE_KART,
};

/**
 * The track route a mount page owns, or undefined for bases that host the
 * whole flow (the booking page, the alias, product pages, dev). The
 * `\d{6}jt$`-style shapes match the numbered preview pages Shopify generates
 * for unpublished duplicates — same convention as `isMainBookingPath`.
 */
const trackRouteForMountPage = (pageHandle?: string): string | undefined => {
  if (!pageHandle) return undefined;
  const owned = MOUNT_PAGE_TRACK_ROUTES[pageHandle];
  if (owned) return owned;
  if (/\d{7}mi$/.test(pageHandle)) return ROUTE_SLUGS.MINI;
  if (/\d{6}jt$/.test(pageHandle)) return ROUTE_SLUGS.JUNIOR;
  if (/\d{6}bk$/.test(pageHandle)) return ROUTE_SLUGS.BATTLE_KART;
  return undefined;
};

/**
 * Query-string vocabulary: descriptive code name → short wire key.
 *
 * The wire keys are deliberately terse because booking URLs get pasted into
 * chats and ads, while the code keeps the readable names — `useUrlParams`
 * translates between the two, so nothing outside this mapping and that hook
 * needs to know the short form. The long names are still accepted on the way in
 * and rewritten to the short form during bootstrap.
 */
export const BOOKING_PARAM_KEYS = {
  numberOfSeats: "seats",
  numberOfRaces: "races",
  selectedDate: "date",
  ageGroup: "age",
  postcode: "pc",
  homepage: "hp",
  redirect: "rd",
  includeBK: "bk",
  picker: "picker",
  discountOffered: "do",
  discount20Applied: "d20",
  discount25Applied: "d25",
  qualifiedFor20: "q20",
  qualifiedFor25: "q25",
  qualifiedFor20OnThird: "q20t",
  bkDiscountOffered: "bkdo",
  bkDiscount20Applied: "bkd20",
  bkDiscount25Applied: "bkd25",
  bkQualifiedFor20: "bkq20",
  bkQualifiedFor25: "bkq25",
  bkQualifiedFor20OnThird: "bkq20t",
} as const;

export type BookingParamName = keyof typeof BOOKING_PARAM_KEYS;

/**
 * Params carrying a boolean. On the wire these are `1`/`0`; in code they stay
 * the `"true"`/`"false"` strings the whole app compares against.
 */
const BOOLEAN_PARAMS = new Set<BookingParamName>([
  "homepage",
  "redirect",
  "includeBK",
  "discountOffered",
  "discount20Applied",
  "discount25Applied",
  "qualifiedFor20",
  "qualifiedFor25",
  "qualifiedFor20OnThird",
  "bkDiscountOffered",
  "bkDiscount20Applied",
  "bkDiscount25Applied",
  "bkQualifiedFor20",
  "bkQualifiedFor25",
  "bkQualifiedFor20OnThird",
]);

/** Code value → wire value. */
export const encodeBookingParamValue = (name: BookingParamName, value: string): string => {
  if (!BOOLEAN_PARAMS.has(name)) return value;
  if (value === "true") return "1";
  if (value === "false") return "0";
  return value;
};

/** Wire value → code value. Legacy `true`/`false` values pass straight through. */
export const decodeBookingParamValue = (name: BookingParamName, value: string): string => {
  if (!BOOLEAN_PARAMS.has(name)) return value;
  if (value === "1") return "true";
  if (value === "0") return "false";
  return value;
};

/**
 * Every query key the booking flow owns, in both the current and the superseded
 * spelling. `campaignParams` uses this to tell app state apart from the
 * marketing params it forwards to Checkout — deriving it here is what stops the
 * two lists drifting when a param is renamed.
 */
export const BOOKING_QUERY_KEYS: ReadonlySet<string> = new Set<string>([
  ...Object.keys(BOOKING_PARAM_KEYS),
  ...Object.values(BOOKING_PARAM_KEYS),
  ...LEGACY_ROUTING_PARAMS,
]);

/**
 * Rewrite superseded query keys to their short form, preserving parameter order
 * and leaving unrecognised (campaign/tracking) params untouched.
 */
const canonicaliseQuery = (params: URLSearchParams): URLSearchParams => {
  const canonical = new URLSearchParams();

  params.forEach((value, key) => {
    // A key already in short form is not in this map, so it passes through.
    const wireKey = BOOKING_PARAM_KEYS[key as BookingParamName];
    if (wireKey) {
      canonical.set(wireKey, encodeBookingParamValue(key as BookingParamName, value));
    } else {
      canonical.append(key, value);
    }
  });

  return canonical;
};

interface SplitPath {
  /** Anything before the base, e.g. a market/locale prefix such as `/en-au`. */
  localePrefix: string;
  /** The Shopify page handle when the URL is a `/pages/<handle>` URL. */
  pageHandle?: string;
  /** True when this URL serves the main booking flow. */
  isBookingPage: boolean;
  /** The in-app router path (leading slash, `/` when at the index). */
  inAppPath: string;
  /** The base path the router should treat as its root. */
  basePath?: string;
}

const stripTrailingSlash = (value: string): string => (value.length > 1 && value.endsWith("/") ? value.replace(/\/+$/, "") : value);

const ensureLeadingSlash = (value: string): string => (value.startsWith("/") ? value : `/${value}`);

/**
 * Break a pathname into the part that identifies the app's mount point and the
 * part that belongs to the in-app router.
 *
 * Handles three shapes:
 *   - Shopify page     `/pages/booking/main/seats` (any mount page)
 *   - pretty alias     `/booking/tracks/main/seats`
 *   - bare legacy path `/main/seats` (only reachable via the theme's 404
 *     redirect, so treated as pure in-app path)
 *
 * A page's own pathname is never swapped for a different one. The alias has no
 * page behind it, so it resolves to the booking page base.
 *
 * The `/pages/` shape is tested first because the alias can appear inside
 * one: `/pages/booking/tracks` (page handle `booking`, route `tracks`) contains
 * the literal `/booking/tracks`, and matching that first would read the whole
 * thing as the base and lose the route.
 */
export const splitBookingPath = (pathname: string): SplitPath => {
  const clean = stripTrailingSlash(pathname || "/");

  const pagesIndex = clean.indexOf("/pages/");
  if (pagesIndex !== -1) {
    const localePrefix = clean.slice(0, pagesIndex);
    const afterPages = clean.slice(pagesIndex + "/pages/".length);
    const [pageHandle, ...restSegments] = afterPages.split("/");
    const isBookingPage = BOOKING_PAGE_HANDLES.includes(pageHandle);
    return {
      localePrefix,
      pageHandle,
      isBookingPage,
      inAppPath: restSegments.length ? `/${restSegments.join("/")}` : "/",
      basePath: `${localePrefix}/pages/${pageHandle}`,
    };
  }

  const aliasIndex = clean.indexOf(BOOKING_ALIAS_PATH);
  if (aliasIndex !== -1) {
    const localePrefix = clean.slice(0, aliasIndex);
    const rest = clean.slice(aliasIndex + BOOKING_ALIAS_PATH.length);
    return {
      localePrefix,
      pageHandle: BOOKING_PAGE_HANDLES[0],
      isBookingPage: true,
      inAppPath: rest || "/",
      basePath: `${localePrefix}${BOOKING_PAGE_PATH}`,
    };
  }

  return {
    localePrefix: "",
    isBookingPage: false,
    inAppPath: clean || "/",
    basePath: undefined,
  };
};

/**
 * Rewrite the route/screen segments of an in-app path to their canonical slugs,
 * leaving anything already canonical (and any deeper segments) untouched.
 */
export const canonicaliseInAppPath = (inAppPath: string): string => {
  const [route, screen, ...rest] = inAppPath.split("/").filter(Boolean);
  if (!route) return "/";

  const segments = [LEGACY_ROUTE_SLUGS[route] ?? route];
  if (screen) segments.push(LEGACY_SCREEN_SLUGS[screen] ?? screen);

  return `/${[...segments, ...rest].join("/")}`;
};

/**
 * Canonical route slug for a pathname, or `undefined` when the URL is not an
 * in-app booking route.
 *
 * Use this instead of substring-matching the pathname. Substring checks were
 * safe against the old slugs but are not against the short ones: the Shopify
 * info page `/pages/intermediate-info-page` contains "intermediate", and
 * `/pages/main-go-karting-track` contains "main".
 */
export const getRouteSlug = (pathname: string): string | undefined => {
  const { inAppPath } = splitBookingPath(pathname);
  const [route] = inAppPath.split("/").filter(Boolean);
  if (!route) return undefined;

  return LEGACY_ROUTE_SLUGS[route] ?? route;
};

/** Read and consume the path stashed by the theme's 404 redirect. */
export const consumeDeepPathHandoff = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.sessionStorage.getItem(DEEP_PATH_STORAGE_KEY);
    if (stored) window.sessionStorage.removeItem(DEEP_PATH_STORAGE_KEY);
    return stored || null;
  } catch {
    // Private mode / disabled storage: fall back to the plain page load.
    return null;
  }
};

export interface ResolvedBookingLocation {
  /** Basename to hand to `BrowserRouter` (undefined outside Shopify, e.g. dev). */
  basename?: string;
  /** Canonical `pathname + search + hash`, or null when the URL is already canonical. */
  canonicalUrl: string | null;
}

interface ResolveInput {
  pathname: string;
  search: string;
  hash?: string;
  /** Path recorded by the theme redirect before it bounced to the real page. */
  handoffPath?: string | null;
}

/**
 * Work out the router basename and the canonical URL for the current location.
 *
 * The in-app path is recovered from the first available source, in order:
 *   1. the path already on the URL,
 *   2. the theme's 404-redirect handoff,
 *   3. the legacy `selectedRoute`/`selectedScreen` query params.
 *
 * (3) is what keeps every pre-existing marketing deep link working: the params
 * are read once here, promoted to path segments, and then removed from the URL.
 *
 * A page's base is never swapped for a different one. The theme's bounce lands
 * on the serving page, so when the handoff carries the full original URL its
 * base is adopted too: an arrival on a page keeps that page, and an arrival
 * via the `/booking/tracks` alias resolves to `/pages/booking`.
 *
 * Returns `canonicalUrl: null` when nothing needs rewriting, so callers can skip
 * a pointless `history.replaceState`.
 */
export const resolveBookingLocation = ({ pathname, search, hash = "", handoffPath = null }: ResolveInput): ResolvedBookingLocation => {
  const current = splitBookingPath(pathname);
  const { inAppPath } = current;
  let basePath = current.basePath;

  const params = new URLSearchParams(search);
  const legacyRoute = params.get("selectedRoute");
  const legacyScreen = params.get("selectedScreen");
  const hadLegacyParams = legacyRoute !== null || legacyScreen !== null;
  LEGACY_ROUTING_PARAMS.forEach((key) => params.delete(key));

  let resolvedInAppPath = inAppPath;

  // The theme only ever bounces to the booking page, so a handoff surfacing on
  // any other page (e.g. a track-specific mount page) is stale by definition —
  // left behind by a bounce whose landing never consumed it. Replaying it
  // there would resurrect a foreign route under the wrong base.
  if (resolvedInAppPath === "/" && handoffPath && current.isBookingPage) {
    const handoff = splitBookingPath(ensureLeadingSlash(handoffPath));
    resolvedInAppPath = handoff.inAppPath;
    if (handoff.isBookingPage && handoff.basePath) {
      basePath = handoff.basePath;
    }
  }

  if (resolvedInAppPath === "/" && legacyRoute) {
    resolvedInAppPath = legacyScreen ? `/${legacyRoute}/${legacyScreen}` : `/${legacyRoute}`;
  }

  // Whichever source the path came from, it may carry the old long slugs.
  resolvedInAppPath = canonicaliseInAppPath(resolvedInAppPath);

  // A track-specific mount page owns its track: a deep link that names a
  // different track under such a page is rewritten to the page's own route,
  // keeping the screen. Keyed on the final base (not the raw request) so a
  // handoff-restored arrival is judged by the page the user actually sees.
  const ownedRoute = trackRouteForMountPage(basePath ? splitBookingPath(basePath).pageHandle : undefined);
  if (ownedRoute) {
    const [route, ...rest] = resolvedInAppPath.split("/").filter(Boolean);
    if (route && TRACK_ROUTE_SLUGS.has(route) && route !== ownedRoute) {
      resolvedInAppPath = `/${[ownedRoute, ...rest].join("/")}`;
    }
  }

  // Old links spell the booking params out in full; promote them to the short
  // keys here so the address bar never shows a mix of both spellings.
  const canonicalParams = canonicaliseQuery(params);
  const queryChanged = canonicalParams.toString() !== params.toString();
  const queryString = canonicalParams.toString();

  // Outside Shopify (local dev, headless embeds) there is no page prefix to
  // rewrite, so leave the path alone and only clean up the query string.
  if (!basePath) {
    return {
      basename: undefined,
      canonicalUrl:
        hadLegacyParams || queryChanged || resolvedInAppPath !== inAppPath ? `${resolvedInAppPath}${queryString ? `?${queryString}` : ""}${hash}` : null,
    };
  }

  const canonicalPath = `${basePath}${resolvedInAppPath === "/" ? "" : resolvedInAppPath}` || "/";
  const canonicalUrl = `${canonicalPath}${queryString ? `?${queryString}` : ""}${hash}`;
  const currentUrl = `${stripTrailingSlash(pathname)}${search}${hash}`;

  return {
    basename: basePath,
    canonicalUrl: canonicalUrl === currentUrl ? null : canonicalUrl,
  };
};

let cachedBasename: { value: string | undefined } | null = null;

/**
 * Canonicalise the browser URL and return the router basename.
 *
 * Must run before `BrowserRouter` mounts (see `src/index.tsx`) so the router
 * reads the final URL and there is no visible flash of the old one.
 *
 * The result is cached because the basename is fixed for the lifetime of the
 * page: re-running it after the router has started navigating would derive a
 * basename from an in-app URL and consume an already-spent handoff.
 */
export const applyCanonicalBookingUrl = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  if (cachedBasename) return cachedBasename.value;

  const { basename, canonicalUrl } = resolveBookingLocation({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    handoffPath: consumeDeepPathHandoff(),
  });

  if (canonicalUrl) {
    window.history.replaceState(window.history.state, "", canonicalUrl);
  }

  cachedBasename = { value: basename };
  return basename;
};

/** Test-only: drop the memoised basename so each case starts clean. */
export const resetCanonicalBookingUrlCache = (): void => {
  cachedBasename = null;
};

/**
 * True when `pathname` is the main booking entry point, on any of the Shopify
 * page URLs that serve it or the `/booking/tracks` alias.
 *
 * The `\d{6}mt` shape matches the numbered preview pages Shopify generates for
 * unpublished duplicates of the booking page.
 */
export const isMainBookingPath = (pathname: string = typeof window !== "undefined" ? window.location.pathname : ""): boolean => {
  const { isBookingPage, pageHandle } = splitBookingPath(pathname);
  if (isBookingPage) return true;
  return /\d{6}mt$/.test(pageHandle ?? stripTrailingSlash(pathname));
};

/** The storefront home, where the booking flow hands visitors back to. */
export const STORE_HOME_URL = "https://hyperkarting.com.au";

/**
 * Where Back lands on the first booking step, which has no earlier screen of
 * its own.
 *
 * Visitors reach that step from the storefront (the homepage's Book now button,
 * or a track details page), so the referrer is the page they actually came from. Anything else falls back to the storefront home: a direct hit or ad
 * click (no referrer), another site (never redirect Back off-domain), or the
 * booking page itself (which would only reload the app).
 */
export const resolveStoreExitUrl = ({ referrer, appOrigin }: { referrer: string; appOrigin: string }): string => {
  if (!referrer) return STORE_HOME_URL;

  let referringUrl: URL;
  try {
    referringUrl = new URL(referrer);
  } catch {
    return STORE_HOME_URL;
  }

  if (referringUrl.origin !== appOrigin) return STORE_HOME_URL;
  if (isMainBookingPath(referringUrl.pathname)) return STORE_HOME_URL;

  return referringUrl.href;
};
